import React, { useMemo } from 'react';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

type PaginationModelItem =
  | { type: 'page'; page: number; key: string }
  | { type: 'ellipsis'; key: string };

function buildPaginationModel(
  currentPage: number,
  totalPages: number,
): PaginationModelItem[] {
  const items: PaginationModelItem[] = [];
  const maxVisiblePages = 4;

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      items.push({ type: 'page', page: i, key: `page-${i}` });
    }
    return items;
  }

  if (currentPage <= 4) {
    for (let i = 1; i <= 5; i++) {
      items.push({ type: 'page', page: i, key: `page-${i}` });
    }
    if (totalPages > 5) {
      items.push({ type: 'ellipsis', key: 'ellipsis-end' });
      items.push({ type: 'page', page: totalPages, key: `page-${totalPages}` });
    }
    return items;
  }

  if (currentPage > totalPages - 4) {
    items.push({ type: 'page', page: 1, key: 'page-1' });
    items.push({ type: 'ellipsis', key: 'ellipsis-start' });
    for (let i = totalPages - 4; i <= totalPages; i++) {
      items.push({ type: 'page', page: i, key: `page-${i}` });
    }
    return items;
  }

  items.push({ type: 'page', page: 1, key: 'page-1' });
  items.push({ type: 'ellipsis', key: 'ellipsis-start' });
  for (let i = currentPage - 2; i <= currentPage + 2; i++) {
    items.push({ type: 'page', page: i, key: `page-${i}` });
  }
  items.push({ type: 'ellipsis', key: 'ellipsis-end' });
  items.push({ type: 'page', page: totalPages, key: `page-${totalPages}` });
  return items;
}

export function PaginationRange({
  currentPage,
  totalPages,
  onPageChange,
  className,
  showIfSinglePage = false,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  showIfSinglePage?: boolean;
}) {
  const safeTotalPages = Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1;
  const safeCurrentPage = Number.isFinite(currentPage)
    ? Math.min(Math.max(1, currentPage), safeTotalPages)
    : 1;

  const model = useMemo(
    () => buildPaginationModel(safeCurrentPage, safeTotalPages),
    [safeCurrentPage, safeTotalPages],
  );

  if (!showIfSinglePage && safeTotalPages <= 1) return null;

  const desktopOnlyClass = 'hidden md:block';
  const mobileOnlyClass = 'md:hidden';

  return (
    <Pagination className={cn(className)}>
      <PaginationContent className='flex-wrap'>
        {/* Mobile: Apenas Previous e Next */}
        <PaginationItem className={mobileOnlyClass}>
          <PaginationPrevious
            href='#'
            onClick={(e) => {
              e.preventDefault();
              if (safeCurrentPage > 1) onPageChange(safeCurrentPage - 1);
            }}
          />
        </PaginationItem>
        <PaginationItem className={mobileOnlyClass}>
          <PaginationNext
            href='#'
            onClick={(e) => {
              e.preventDefault();
              if (safeCurrentPage < safeTotalPages) onPageChange(safeCurrentPage + 1);
            }}
          />
        </PaginationItem>

        {/* Desktop: Range com reticências */}
        <PaginationItem className={desktopOnlyClass}>
          <PaginationPrevious
            href='#'
            onClick={(e) => {
              e.preventDefault();
              if (safeCurrentPage > 1) onPageChange(safeCurrentPage - 1);
            }}
          />
        </PaginationItem>

        {model.map((item) => {
          if (item.type === 'ellipsis') {
            return (
              <PaginationItem key={item.key} className={desktopOnlyClass}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }

          return (
            <PaginationItem key={item.key} className={desktopOnlyClass}>
              <PaginationLink
                href='#'
                isActive={safeCurrentPage === item.page}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(item.page);
                }}
              >
                {item.page}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem className={desktopOnlyClass}>
          <PaginationNext
            href='#'
            onClick={(e) => {
              e.preventDefault();
              if (safeCurrentPage < safeTotalPages) onPageChange(safeCurrentPage + 1);
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

